import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { IlsModelService } from '../../../../shared/services/ils-model.service'
import { pairwise } from 'rxjs'
import { AutoResizeElementService, VideoUtilsService } from '@monorepo/angular-common'
import { Content } from '@monorepo/golab-generated'
import { YouTubePlayer } from '@angular/youtube-player'

const vimeoPlayerOrigin = 'https://player.vimeo.com'

@Component({
  selector: 'app-video-content',
  templateUrl: './video-content.component.html',
  styleUrls: ['./video-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [YouTubePlayer],
})
export class VideoContentComponent implements OnDestroy {
  private readonly domSanitizer = inject(DomSanitizer)
  private readonly videoUtilsService = inject(VideoUtilsService)

  videoUrl: SafeResourceUrl
  youTube = true
  vimeo = false
  youTubeVideoPlayer: YT.Player | undefined
  @ViewChild('youtubePlayerRef') youtubePlayer?: YouTubePlayer
  youTubeVideoPlayerVars: YT.PlayerVars = {
    autoplay: 0,
    controls: 1,
    // disablekb: 1,
    enablejsapi: 1,
    fs: 1,
    iv_load_policy: 3,
    modestbranding: 1,
    rel: 0,
    showinfo: 0,
  }
  videoId: string | undefined = undefined
  width = 640
  height = 360
  shouldShowCustomThumbnail = false
  @Input() phaseId: string = ''
  @ViewChild('standardVideoPlayer') standardVideoPlayer: ElementRef<HTMLVideoElement> | undefined
  @ViewChild('vimeoVideoPlayer') vimeoVideoPlayer: ElementRef<HTMLIFrameElement> | undefined
  private autoResizeElementService: AutoResizeElementService

  constructor() {
    const ilsModelService = inject(IlsModelService)
    const changeDetectorRef = inject(ChangeDetectorRef)

    this.videoUrl = this.domSanitizer.bypassSecurityTrustResourceUrl('')
    ilsModelService.currentPhaseId$.pipe(pairwise()).subscribe(([previousPhaseId, newPhaseId]) => {
      // console.log(`Going from ${previousPhaseId} to ${newPhaseId}`, this.leavingMyPhase(previousPhaseId, newPhaseId))
      if (this.leavingMyPhase(previousPhaseId, newPhaseId)) {
        this.pauseVideo()
      }
    })
    this.autoResizeElementService = new AutoResizeElementService(ilsModelService, (size) => {
      console.log('newSize$', size)
      setTimeout(() => {
        this.width = size.width
        this.height = size.height
        changeDetectorRef.detectChanges()
      })
    })
    this.autoResizeElementService.setDesiredSize(1920, 1080)
  }

  private _content: Content | undefined

  get content(): Content | undefined {
    return this._content
  }

  @Input() set content(content: Content | undefined) {
    if (content) {
      this.autoResizeElementService.setReferenceId(content._id)
      this.setVideoUrl(content.url ? content.url : '')
    }
    this._content = content
  }

  ngOnDestroy(): void {
    this.autoResizeElementService.destroy()
  }

  youtubeStateChange($event: YT.OnStateChangeEvent) {
    // console.log('youtubeStateChange', $event)
    this.youTubeVideoPlayer = $event.target
    // this.youTubeVideoPlayer.setSize(1920, 1080)
  }

  playVideo() {
    this.youtubePlayer?.playVideo() // <-- trigger playback
    this.shouldShowCustomThumbnail = false
  }

  private leavingMyPhase(previousPhaseId: string | null, newPhaseId: string | null): boolean {
    return this.phaseId === previousPhaseId && this.phaseId !== newPhaseId
  }

  onLoad(event: Event) {
    const img = event.target as HTMLImageElement

    // Check if it's the YouTube placeholder
    if (img.naturalWidth === 120 && img.naturalHeight === 90) {
      this.shouldShowCustomThumbnail = true
    } else {
      this.shouldShowCustomThumbnail = false
    }
  }

  private setVideoUrl(url: string): void {
    this.youTube = false
    this.vimeo = false
    this.autoResizeElementService.setAutoResize(false)
    if (this.videoUtilsService.isYoutubeUrl(url)) {
      this.youTube = true
      const videoId = this.videoUtilsService.getYouTubeVideoId(url)
      if (videoId) {
        this.videoId = videoId
        this.autoResizeElementService.setAutoResize(true)
        // this.videoUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}`)
      }
    } else if (this.videoUtilsService.isVimeoUrl(url)) {
      this.vimeo = true
      const videoId = this.videoUtilsService.getVimeoVideoId(url)
      if (videoId) {
        this.videoUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${vimeoPlayerOrigin}/video/${videoId}`)
      }
    } else {
      this.videoUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url)
    }
  }

  private pauseVideo() {
    if (!this.content?.url) {
      return
    }
    // console.log('pauseVideo', this.content?.url)
    if (this.youTube) {
      this.pauseYouTubeVideo()
      return
    }
    if (this.vimeo) {
      this.pauseVimeoVideo()
      return
    }
    this.pauseStandardVideo()
  }

  private pauseYouTubeVideo() {
    // console.log('pauseYouTubeVideo')
    this.youTubeVideoPlayer?.pauseVideo()
  }

  private pauseVimeoVideo() {
    // The Vimeo embed accepts player.js API commands via postMessage, so no SDK is needed
    this.vimeoVideoPlayer?.nativeElement.contentWindow?.postMessage({ method: 'pause' }, vimeoPlayerOrigin)
  }

  private pauseStandardVideo() {
    // console.log('pauseStandardVideo', this.standardVideoPlayer)
    if (this.standardVideoPlayer) {
      const videoPlayer = this.standardVideoPlayer.nativeElement
      // if video is playing, pause it
      if (!videoPlayer.paused) {
        videoPlayer.pause()
      }
    }
  }
}
